import { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Spinner } from "react-bootstrap";
import { FiMoreVertical } from "react-icons/fi";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
const DEFAULT_PROFILE_IMAGE =
  "https://cdn.jsdelivr.net/gh/twbs/icons@1.10.0/icons/person-circle.svg";

const UserAccountDeactivated = () => {
  const [deactivatedEmployees, setDeactivatedEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    const fetchDeactivatedEmployees = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/deactivated-employees`);
        if (response.status === 200) {
          const data = response.data.results.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setDeactivatedEmployees(data);
        }
      } catch (error) {
        console.error("Error fetching deactivated employees:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDeactivatedEmployees();
  }, []);

  const getProfileSrc = (emp) =>
    emp.photo_url?.startsWith("http")
      ? emp.photo_url
      : emp.image_filename
      ? `${API_URL}/uploads/${emp.image_filename}`
      : DEFAULT_PROFILE_IMAGE;

  const toggleMenu = (id) => {
    setActiveMenuId(activeMenuId === id ? null : id);
  };

  useEffect(() => {
    const handleClickOutside = () => setActiveMenuId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleActivateClick = (employee) => {
    setSelectedEmployee(employee);
    setShowModal(true);
    setActiveMenuId(null);
  };

  const confirmActivation = async () => {
    if (!selectedEmployee) return;
    try {
      await axios.post(`${API_URL}/api/activate-employee`, {
        name: selectedEmployee.name,
      });
      setDeactivatedEmployees((prev) =>
        prev.filter((emp) => emp.name !== selectedEmployee.name)
      );
    } catch (err) {
      console.error("Error activating employee:", err);
    }
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (deactivatedEmployees.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        No deactivated employees found.
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          maxHeight: "75vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))",
          gap: "16px",
          padding: "4px 4px 12px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {deactivatedEmployees.map((employee) => (
          <div
            key={employee.name}
            style={{
              background: "#fee2e2",
              borderRadius: "14px",
              border: "1px solid #fca5a5",
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
              padding: "20px 12px 16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              position: "relative",
            }}
          >
            {/* Three-dot menu */}
            <div
              style={{ position: "absolute", top: "10px", right: "10px", cursor: "pointer" }}
              onClick={(e) => { e.stopPropagation(); toggleMenu(employee.name); }}
            >
              <FiMoreVertical size={18} color="#94a3b8" />
            </div>

            {/* Dropdown */}
            {activeMenuId === employee.name && (
              <div
                style={{
                  position: "absolute",
                  top: "32px",
                  right: "10px",
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  zIndex: 100,
                  minWidth: "150px",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  onClick={() => handleActivateClick(employee)}
                  style={{
                    padding: "8px 14px",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    color: "#22c55e",
                    whiteSpace: "nowrap",
                  }}
                >
                  Activate Account
                </div>
              </div>
            )}

            {/* Profile photo */}
            <img
              src={getProfileSrc(employee)}
              alt={employee.name}
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #fca5a5",
                marginBottom: "12px",
              }}
              onError={(ev) => {
                ev.target.onerror = null;
                ev.target.src = DEFAULT_PROFILE_IMAGE;
              }}
            />

            {/* Name */}
            <div style={{ fontWeight: "700", fontSize: "0.875rem", color: "#0f172a", marginBottom: "2px", lineHeight: 1.3 }}>
              {employee.name}
            </div>

            {/* Location */}
            <div style={{ fontSize: "0.78rem", color: "#6b7280", marginBottom: "10px", fontWeight: "400" }}>
              {employee.location}
            </div>

            {/* Status pill */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "4px 12px",
                borderRadius: "999px",
                border: "1px solid #fca5a5",
                background: "#fff",
                fontSize: "0.78rem",
                fontWeight: "500",
                color: "#475569",
              }}
            >
              <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#ef4444", flexShrink: 0 }} />
              Deactivated
            </div>
          </div>
        ))}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header>
          <Modal.Title>Activate Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmployee && (
            <p>Are you sure you want to activate <strong>{selectedEmployee.name}</strong>?</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="success" onClick={confirmActivation}>Activate</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserAccountDeactivated;
